import React, { useEffect, useState, useRef, memo } from "react";
import Header from "./Header";
import { useDispatch, useSelector } from "react-redux";
import { discardSentenceForUser, getRejectedSentencesInALotTTT, getSentencesInALot, rejectSentenceForUser, resolveSentenceForUser, reviewSentenceForStudent, validateSentenceForStudent } from "@/store/thunks/studentTTT";
import { toast } from "react-toastify";
import apiClient from "@/services/api/apiClientStudent";
import { BASE_URL } from "@/utils/constants";
import GenericTableStudent from "../GenericTableStudent";

const ReviewModal = memo(function ReviewModal({
  isOpen,
  row,
  comment,
  onCommentChange,
  selectedTargetName,
  onClose,
  onSubmit,
  translatedText,
  loading
}) {
  if (!isOpen || !row) return null;
  console.log("Opening Review Modal for row:", row);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-screen overflow-y-auto">
        <div className="p-6">
          <h3 className="text-xl font-bold text-gray-800 mb-4">Add Review</h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Source (English)
              </label>
              <p className="p-3 bg-gray-50 rounded-lg text-gray-800 whitespace-pre-wrap">
                {row.original.sourceText}
              </p>
            </div>


            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Translation Text ({`${selectedTargetName}`})
              </label>
              <p className="p-3 bg-blue-50 rounded-lg text-gray-800 whitespace-pre-wrap border">
                {translatedText || "<empty>"}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Your Review / Comment <span className="text-red-500"> (Comment must contain at least 8 words.) *</span>
              </label>
              <textarea
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none resize-none"
                rows="5"
                value={comment}
                onChange={(e) => onCommentChange(e.target.value)}
                placeholder="Write your feedback, suggestions, or corrections here..."
              />
            </div>
          </div>

          <div className="flex gap-3 justify-end mt-6">
            <button
              onClick={onClose}
              className="px-5 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition"
            >
              Cancel
            </button>
            <button
              onClick={onSubmit}
              disabled={loading}
              className="px-6 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium transition disabled:opacity-70"
            >
              {loading ? "Submitting..." : "Submit Review"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

const CommentsModal = memo(function CommentsModal({
  isOpen,
  comments = [],
  onClose,
  loading = false
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-screen overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6 border-b pb-4">
            <h3 className="text-2xl font-bold text-gray-800">Review Comments</h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-3xl font-light hover:scale-110 transition"
            >
              ×
            </button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
              <p className="mt-4 text-gray-600">Loading comments...</p>
            </div>
          ) : comments.length === 0 ? (
            <div className="text-center py-16 bg-gray-50 rounded-lg">
              <p className="text-gray-500 text-lg">No review comments yet.</p>
            </div>
          ) : (
            <div className="space-y-5">
              {comments.map((c, i) => (
                <div
                  key={i}
                  className="bg-gradient-to-r from-gray-50 to-gray-100 p-5 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                        {c.studentUsername?.[0]?.toUpperCase() || "S"}
                      </div>
                      <div>
                        <p className="font-semibold text-gray-800">
                          {c.studentUsername || "Unknown Student"}
                        </p>
                        <p className="text-sm text-amber-600 font-medium">
                          Target: {c.targetName || "Unknown"}
                        </p>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500 bg-gray-200 px-3 py-1 rounded-full">
                      {new Date(c.createdAt).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit"
                      })}
                    </span>
                  </div>

                  <div className="mt-3 pl-13">
                    <p className="text-gray-800 whitespace-pre-wrap leading-relaxed bg-white p-4 rounded-lg border-l-4 border-amber-400">
                      {c.comment}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

const DiscardModal = memo(function DiscardModal({
  isOpen,
  row,
  onConfirm,
  onCancel,
  loading = false
}) {
  if (!isOpen || !row) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-red-600 text-white p-6 text-center">
          <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold">Discard This Sentence?</h3>
          <p className="mt-2 text-red-100">This action cannot be undone.</p>
        </div>

        <div className="p-6 bg-gray-50">
          <div className="space-y-3 text-gray-700">
            <div className="flex gap-2">
              <span className="font-medium">Sentence ID:</span>
              <span className="font-mono font-bold">#{row.original.sentenceId}</span>
            </div>
            <div className="flex gap-2">
              <span className="font-medium">Source Text:</span>
            </div>
            <p className="text-sm bg-white p-4 rounded-lg border border-gray-200 whitespace-pre-wrap max-h-32 overflow-y-auto">
              {row.original.sourceText}
            </p>
          </div>

          <div className="flex gap-4 mt-8">
            <button
              onClick={onCancel}
              disabled={loading}
              className="flex-1 px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-xl transition"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              className="text-md flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl shadow-lg transition transform hover:scale-105 disabled:opacity-70"
            >
              {loading ? "Discarding..." : "Yes, Discard Sentence"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});


const TTTCard = () => {
  const dispatch = useDispatch();
  const { selectedLotId, selectedTargetId, selectedTargetName, showMarkedSentencesTTT, editSentencesTTS } = useSelector((state) => state.studentDashboard);
  const { userId, role } = useSelector((state) => state.studentAuth);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);
  const [doubleClickedRow, setDoubleClickedRow] = useState(null);
  const [activeTab, setActiveTab] = useState("active"); // active | rejected
  const [activeConfirmation, setActiveConfirmation] = useState(null);
  const editedRowsRef = useRef(new Set());
  // Helper to check if row is dirty (edited after validation)
  const isRowEdited = (sentenceId) => editedRowsRef.current.has(sentenceId);

  // Helper to mark row as edited
  const markRowAsEdited = (sentenceId) => {
    if (!editedRowsRef.current.has(sentenceId)) {
      editedRowsRef.current.add(sentenceId);
      setDataVersion(v => v + 1);
    }
  };

  // Helper to clear edited flag after successful validation
  const clearEditedFlag = (sentenceId) => {
    editedRowsRef.current.delete(sentenceId);
  };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [files, setFiles] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [dataVersion, setDataVersion] = useState(0);
  const [reviewModal, setReviewModal] = useState({
    isOpen: false,
    row: null,        // the row being reviewed
    comment: ""       // user's review comment
  });

  const [commentsModal, setCommentsModal] = useState({
    isOpen: false,
    sentenceId: null,
    comments: [],
    loading: false
  });

  const [discardModal, setDiscardModal] = useState({
    isOpen: false,
    row: null,
    loading: false
  });

  // Add this line near your other useState
  const tableDataRef = React.useRef(tableData);
  const activeTabRef = useRef(activeTab);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);


  // Add this effect — THIS IS THE KEY!
  useEffect(() => {
    tableDataRef.current = tableData;
  }, [tableData]);

  useEffect(() => {
    setPageIndex(0);
    setPageSize(10);
    setSelectedRow(null);
  }, [activeTab]);


  // ← ADD THIS HOOK (anywhere inside TTTCard)
  useEffect(() => {
    const isAnyModalOpen = reviewModal.isOpen || commentsModal.isOpen || !!activeConfirmation;

    if (isAnyModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [reviewModal.isOpen, commentsModal.isOpen, activeConfirmation]);



  const paginatedData = React.useMemo(() => {
    const start = pageIndex * pageSize;
    const end = start + pageSize;
    return files.slice(start, end);
  }, [files, pageIndex, pageSize]);



  const hydrateTableData = (freshData) => {
    const mapped = freshData.map(item => ({
      ...item,
      targetText: item.translatedText || "",
      editableSourceText: item.sourceText || ""
    }));

    setFiles(freshData);
    setTableData(mapped);
    tableDataRef.current = mapped;   // 🔥 THIS LINE IS CRITICAL
    setDataVersion(v => v + 1);
  };



  const getsentencesForUser = async () => {
    setLoading(true);
    try {
      const res = await dispatch(getSentencesInALot(selectedLotId, selectedTargetId));
      if (res.data && Array.isArray(res.data)) {
        hydrateTableData(Array.isArray(res.data) ? res.data : []);;
      } else {
        setFiles([]);
        setTableData([]);
      }
    } catch (err) {
      toast.error("Failed to load sentences.");
    } finally {
      setLoading(false);
    }
  };

  const getRejectedSentencesForUser = async () => {
    setLoading(true);
    try {
      const res = await dispatch(getRejectedSentencesInALotTTT(selectedLotId, selectedTargetId));
      if (res.data && Array.isArray(res.data)) {
        hydrateTableData(Array.isArray(res.data) ? res.data : []);;
      } else {
        setFiles([]);
        setTableData([]);
      }
    } catch (err) {
      toast.error("Failed to load sentences.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch data from API
  useEffect(() => {
    if (activeTab === "active") {
      getsentencesForUser();
    } else if (activeTab === "rejected") {
      getRejectedSentencesForUser();
    }
  }, [dispatch, selectedLotId, selectedTargetId, activeTab]);


  const validateSentence = async (row) => {
    setActiveConfirmation({ type: "validate", row });

    toast.info(
      ({ closeToast }) => (
        <div className="flex flex-col items-center text-center px-4 py-2">
          <p className="mb-4 text-gray-800 text-sm sm:text-base font-medium">
            Do you want to validate this sentence?
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={async () => {
                closeToast();
                await handleValidation(row);
                setActiveConfirmation(null);
              }}
              className="px-4 py-1.5 bg-[#ec7026] hover:bg-[#d15c14] text-white rounded-md font-medium text-sm shadow-sm transition-all duration-200"
            >
              Yes, Validate
            </button>
            <button
              onClick={() => {
                closeToast();
                setActiveConfirmation(null);
              }}
              className="px-4 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md font-medium text-sm shadow-sm transition-all duration-200"
            >
              Cancel
            </button>
          </div>
        </div>
      ),
      {
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        position: "top-center",
        hideProgressBar: true,
        toastId: "validate-confirmation",
        onClose: () => {
          // This runs when toast is dismissed by any means (X button, etc.)
          setActiveConfirmation(null);
        }
      }
    );

    const handleValidation = async (row) => {
      setLoading(true);
      try {
        const absoluteIndex = pageIndex * pageSize + row.index;
        const payload = {
          sentenceId: row.original.sentenceId,
          targetId: row.original.targetId,
          translatedText: tableDataRef.current[absoluteIndex]?.targetText?.trim() || ""
        };

        const res = await dispatch(validateSentenceForStudent(payload));

        if (res.success) {
          toast.success("Sentence validated successfully!");
          // Clear edited flag for this row
          clearEditedFlag(row.original.sentenceId);
          console.log(activeTab)
          if (activeTabRef.current === "active") {
            console.log('active sentences')
            await getsentencesForUser();
          } else {
            console.log('rejected sentences')
            setFiles(prev => prev.filter(item => item.sentenceId !== row.original.sentenceId));
            setTableData(prev => prev.filter(item => item.sentenceId !== row.original.sentenceId));
          }
        } else {
          toast.error(res.message || "Validation failed");
        }
      } catch (err) {
        toast.error("Failed to validate. Try again.");
      } finally {
        setActiveConfirmation(null);
        setLoading(false);
      }
    };
  };

  const reviewSentence = (row) => {
    const absoluteIndex = pageIndex * pageSize + row.index;
    const currentText = tableDataRef.current[absoluteIndex]?.targetText || "";

    if (!currentText.trim()) {
      toast.error("Please enter a translation before adding a review.");
      return;
    }

    setReviewModal({
      isOpen: true,
      row,
      comment: ""
    });
  };

  const showReviewComments = async (row) => {
    const sentenceId = row.original.sentenceId;

    setCommentsModal({
      isOpen: true,
      sentenceId,
      comments: [],
      loading: true
    });

    try {
      const url = role === 'STUDENT' ? `api/student/tt/comments?sentenceId=${sentenceId}` : `api/reviewer/tt/comments?sentenceId=${sentenceId}`;
      // Replace this with your real API endpoint
      const response = await apiClient(BASE_URL + url);

      setCommentsModal(prev => ({
        ...prev,
        comments: response.data || [],
        loading: false
      }));
    } catch (err) {
      toast.error("Failed to load comments.");
      setCommentsModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleReviewSubmit = async () => {
    if (!reviewModal.comment.trim()) {
      toast.error("Please write a review comment.");
      return;
    }

    setLoading(true);
    try {
      const absoluteIndex = pageIndex * pageSize + reviewModal.row.index;
      const translatedText = tableDataRef.current[absoluteIndex]?.targetText || "";

      const payload = {
        sentenceId: reviewModal.row.original.sentenceId,
        targetId: reviewModal.row.original.targetId,
        comment: reviewModal.comment.trim(),
      };

      const res = await dispatch(reviewSentenceForStudent(payload));

      if (res.success) {
        toast.success("Review submitted successfully!");
        await getsentencesForUser();
        setReviewModal({ isOpen: false, row: null, comment: "" });
      } else {
        toast.error(res.message)
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCommentChange = (newComment) => {
    setReviewModal(prev => ({ ...prev, comment: newComment }));
  };

  const closeReviewModal = () => {
    setReviewModal({ isOpen: false, row: null, comment: "" });
  };

  const rejectSentence = async (row) => {

    setActiveConfirmation({ type: "reject", row });

    toast.info(
      ({ closeToast }) => (
        <div className="flex flex-col items-center text-center px-4 py-2">
          <p className="mb-4 text-gray-800 text-md font-medium">
            Do you want to reject this sentence?
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={async () => {
                closeToast();
                await handleReject(row);
                setActiveConfirmation(null);
              }}
              className="px-4 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md font-medium text-sm shadow-sm"
            >
              Yes, Reject
            </button>
            <button
              onClick={() => {
                closeToast();
                setActiveConfirmation(null);
              }}
              className="px-4 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md font-medium text-sm shadow-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      ),
      {
        autoClose: false, closeOnClick: false, position: "top-center", hideProgressBar: true, toastId: "reject-confirmation",
        onClose: () => {
          // This runs when toast is dismissed by any means (X button, etc.)
          setActiveConfirmation(null);
        }
      }
    );

    const handleReject = async (row) => {
      setLoading(true);
      try {
        const res = await dispatch(rejectSentenceForUser(row.original.sentenceId, row.original.targetId, row.original.lotId));
        if (res.success) {
          toast.success("Sentence rejected successfully!");
          await getsentencesForUser();
        } else {
          toast.error(res.message);
        }
      } catch (err) {
        toast.error("Failed to reject.");
      } finally {
        setLoading(false);
        setActiveConfirmation(null);
      }
    };
  };

  const discardSentence = async (row) => {

    setActiveConfirmation({ type: "discard", row });

    toast.info(
      ({ closeToast }) => (
        <div className="flex flex-col items-center text-center px-4 py-2">
          <p className="mb-4 text-gray-800 text-md font-medium">
            Do you want to discard this sentence?
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={async () => {
                closeToast();
                await handleDiscard(row);
                setActiveConfirmation(null);
              }}
              className="px-4 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md font-medium text-sm shadow-sm"
            >
              Yes, Discard
            </button>
            <button
              onClick={() => {
                closeToast();
                setActiveConfirmation(null);
              }}
              className="px-4 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md font-medium text-sm shadow-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      ),
      {
        autoClose: false, closeOnClick: false, position: "top-center", hideProgressBar: true, toastId: "reject-confirmation",
        onClose: () => {
          // This runs when toast is dismissed by any means (X button, etc.)
          setActiveConfirmation(null);
        }
      }
    );

    const handleDiscard = async (row) => {
      setLoading(true);
      setError(null);

      try {
        const absoluteIndex = pageIndex * pageSize + row.index;

        const res = await dispatch(discardSentenceForUser(row.original.sentenceId, row.original.targetId));

        if (res.success) {
          toast.success("🎉 Sentence discarded successfully!", {
            position: "top-center",
            autoClose: 2000,
          });
          await getsentencesForUser();
          setDataVersion(v => v + 1); // ← Force remount
          setDoubleClickedRow(null);
        } else {
          toast.error(`⚠️ Failed: ` + res.message, {
            position: "top-center",
          });
        }
      } catch (err) {
        console.error("❌ Error discard sentence:", err);
        toast.error("⚠️ Failed to discard. Try again.", {
          position: "top-center",
        });
        setDoubleClickedRow(null);
      } finally {
        setLoading(false);
        setActiveConfirmation(null);
      }
    }
  }

  const markAsResolved = async (row) => {
    setActiveConfirmation({ type: "markAsResolved", row });

    // 🟠 Ask user for confirmation
    toast.info(
      ({ closeToast }) => (
        <div className="flex flex-col items-center text-center px-4 py-2">
          <p className="mb-4 text-gray-800 text-md sm:text-base font-medium">
            Do you want to mark this sentence as resolved?
          </p>

          <div className="flex gap-3 justify-center">
            {/* YES BUTTON */}
            <button
              onClick={async () => {
                closeToast();
                await handleMarkAsResolved();
                setActiveConfirmation(null);
              }}
              className="px-2 py-1.5 bg-[#ec7026] hover:bg-[#d15c14] text-white rounded-md font-medium text-sm shadow-sm transition-all duration-200"
            >
            Yes, mark resolved
            </button>

            {/* CANCEL BUTTON */}
            <button
              onClick={() => {
                closeToast();
                setActiveConfirmation(null);
              }}
              className="px-4 py-1.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md font-medium text-sm shadow-sm transition-all duration-200"
            >
              Cancel
            </button>
          </div>
        </div>
      ),
      {
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        position: "top-center",
        hideProgressBar: true,
        toastId: "reject-confirmation",
        onClose: () => {
          // This runs when toast is dismissed by any means (X button, etc.)
          setActiveConfirmation(null);
        }
      }
    );

    // 🔹 Helper: Actual API call
    const handleMarkAsResolved = async () => {
      setLoading(true);
      setError(null);

      try {
        const absoluteIndex = pageIndex * pageSize + row.index;

        const payload = {
          sentenceId: row.original.sentenceId,
          userId: userId,
          targetId: row.original.targetId,
          lotId: row.original.lotId,
          sourceText: tableDataRef.current[absoluteIndex]?.editableSourceText?.trim() || row.original.sourceText
        };

        const res = await dispatch(resolveSentenceForUser(payload));

        if (res.success) {
          toast.success("🎉 Sentence marked as resolved successfully!", {
            position: "top-center",
            autoClose: 2000,
          });
          await getsentencesForUser();
          setDataVersion(v => v + 1); // ← Force remount
        } else {
          toast.error(res.message);
        }
      } catch (err) {
        console.error("❌ Error resolving sentence:", err);
        toast.error("⚠️ Failed to mark as resolved. Try again.", {
          position: "top-center",
        });
      } finally {
        setLoading(false);
        setActiveConfirmation(null);
      }
    };
  };


  const fileColumns = React.useMemo(() => {
    const columns = [
      {
        header: "Sr. No.",
        cell: ({ row }) => pageIndex * pageSize + row.index + 1,
        meta: { width: "10%" },
      },
      {
        header: "Source Text (English)",
        cell: ({ row }) => (
          <span onClick={() => {
            if (row.original.sentenceColor === 'blue') {
              showReviewComments(row);
            }
          }}
            className={`font-medium block whitespace-pre-wrap ${row.original.sentenceColor === 'blue' ? `text-blue-500 hover:text-blue-600 cursor-pointer` : row.original.sentenceColor === 'green' ? `text-green-500 hover:text-green-600` : `text-gray-800`}`}>
            {row.original.sourceText}
          </span>
        ),
        meta: { width: "35%" },
      },
      {
        header: `Target Text (${selectedTargetName})`,
        cell: ({ row }) => {
          const absoluteIndex = pageIndex * pageSize + row.index;

          // Use the ref — this is always up-to-date without causing re-renders
          const value = tableDataRef.current[absoluteIndex]?.targetText || "";

          if (showMarkedSentencesTTT) {
            return (
              <span>{row.original.translatedText}</span>
            )
          }

          return (
            <textarea
              rows={3}
              className="min-h-[60px] w-full border border-gray-300 rounded-md text-gray-700 p-2
          focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none resize-y"
              value={value}
              onChange={(e) => {
                const newValue = e.target.value;

                // Update state (triggers re-render)
                setTableData(prev => {
                  const updated = [...prev];
                  updated[absoluteIndex] = {
                    ...updated[absoluteIndex],
                    targetText: newValue,
                  };
                  return updated;
                });

                // Also immediately update the ref so next render sees correct value
                tableDataRef.current[absoluteIndex].targetText = newValue;
                markRowAsEdited(row.original.sentenceId);
              }}
              placeholder="Enter translation..."
            />
          );
        },
        meta: { width: "35%" },
      },
      {
        header: "Student Status",
        accessorKey: "studentStatus",
        cell: ({ row }) => (
          <span
            className={`font-semibold ${row.original.studentStatus === "PENDING"
              ? "text-yellow-600"
              : row.original.studentStatus === "VALIDATED"
                ? "text-green-600"
                : "text-gray-800"
              }`}
          >
            {row.original.studentStatus || "PENDING"}
          </span>
        ),
        meta: { width: "10%" },
      },
      role === "REVIEWER"
        ? {
          header: "Reviewer Status",
          accessorKey: "reviewerStatus",
          cell: ({ row }) => (
            <span
              className={`font-semibold ${row.original.reviewerStatus === "PENDING"
                ? "text-yellow-600"
                : row.original.reviewerStatus === "VALIDATED"
                  ? "text-green-600"
                  : "text-gray-800"
                }`}
            >
              {row.original.reviewerStatus || "PENDING"}
            </span>
          ),
          meta: { width: "10%" },
        }
        : null,
      {
        header: "Actions",
        cell: ({ row }) => {
          const absoluteIndex = pageIndex * pageSize + row.index;
          const isSelected = absoluteIndex === selectedRow;

          const sentenceId = row.original.sentenceId;
          const isValidated = row.original.studentStatus === 'VALIDATED';
          const isEdited = isRowEdited(sentenceId);           // ← uses ref
          const shouldBeActive = !isValidated || isEdited;

          return (
            role === 'REVIEWER' ?
              <div className="flex flex-col gap-2">
                {row.original.studentStatus === 'REVIEWED' ?
                  <>
                    <button onClick={() => discardSentence(row)}
                      className={`px-4 py-1.5 rounded-md text-sm font-semibold text-white shadow-md transition-all bg-green-600 hover:bg-green-700`}>
                      Discard Sentence
                    </button>
                    <button
                      onClick={() => markAsResolved(row)}
                      className="px-4 py-1.5 rounded-md text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 shadow-md hover:shadow-lg transition-all"
                    >
                      Mark as Resolved
                    </button>
                  </>
                  :
                  <>
                    <button onClick={() => validateSentence(row)}
                      className={`px-4 py-1.5 rounded-md text-sm font-semibold text-white shadow-md transition-all ${row.original.reviewerStatus === 'VALIDATED' ? 'cursor-not-allowed bg-gray-400 hover:bg-gray-500' : 'bg-green-600 hover:bg-green-700'}`}>
                      {row.original.reviewerStatus === 'VALIDATED' ? 'Validated' : row.original.reviewerStatus === 'SAVED' ? "Submit" : "Validate"}
                    </button>
                    <button
                      onClick={() => rejectSentence(row)} className="px-4 py-1.5 rounded-md text-sm font-semibold text-white bg-red-500 hover:bg-red-600 shadow-md hover:shadow-lg transition-all"
                    >Reject
                    </button>
                  </>
                }
              </div>
              :
              <div className="flex flex-col gap-2">
                {row.original.studentStatus !== 'REVIEWED' && <button onClick={() => validateSentence(row)}
                  disabled={!shouldBeActive || showMarkedSentencesTTT}
                  className={`px-4 py-1.5 rounded-md text-sm font-semibold text-white shadow-md transition-all ${!shouldBeActive || showMarkedSentencesTTT ? 'cursor-not-allowed bg-gray-400 hover:bg-gray-500' : 'bg-green-600 hover:bg-green-700'}`}>
                  {shouldBeActive ? 'Validate' : 'Validated'}
                </button>}
                {(row.original.studentStatus === 'PENDING' && row.original.sentenceColor !== 'green') && <button
                  onClick={() => reviewSentence(row)}
                  disabled={showMarkedSentencesTTT}
                  className={`px-4 py-1.5 rounded-md text-sm font-semibold text-white shadow-md transition-all ${showMarkedSentencesTTT
                    ? 'cursor-not-allowed bg-gray-400 hover:bg-gray-500 opacity-60'
                    : 'bg-amber-600 hover:bg-amber-700'
                    }`}
                >Add Review</button>}
              </div>
          );
        }
      }
    ]

    return columns.filter(Boolean);
  }, [pageIndex, pageSize, selectedTargetName, selectedRow, role]);
  // ← tableData REMOVED from deps!

  const reviewerfileColumns = React.useMemo(() => {
    const columns = [
      {
        header: "Sr. No.",
        cell: ({ row }) => pageIndex * pageSize + row.index + 1,
        meta: { width: "10%" },
      },
      {
        header: "Source Text (English)",
        cell: ({ row }) => {
          const absoluteIndex = pageIndex * pageSize + row.index;
          const value = tableDataRef.current[absoluteIndex]?.editableSourceText || "";

          if (row.original.studentStatus === 'REVIEWED') {
            return (
              <textarea
                rows={3}
                className="min-h-[60px] w-full border border-gray-300 rounded-md text-gray-700 p-2
      focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none resize-y"
                value={value}
                onChange={(e) => {
                  const newValue = e.target.value;

                  setTableData(prev => {
                    const updated = [...prev];
                    updated[absoluteIndex] = {
                      ...updated[absoluteIndex],
                      editableSourceText: newValue,
                    };
                    return updated;
                  });

                  tableDataRef.current[absoluteIndex].editableSourceText = newValue;
                }}
                placeholder="Edit source text..."
              />
            )
          }
          return (
            <span onClick={() => {
              if (row.original.sentenceColor === 'blue') {
                showReviewComments(row);
              }
            }}
              className={`font-medium block whitespace-pre-wrap ${row.original.sentenceColor === 'blue' ? `text-blue-500 hover:text-blue-600 cursor-pointer` : row.original.sentenceColor === 'green' ? `text-green-500 hover:text-green-600` : `text-gray-800`}`}>
              {row.original.sourceText}
            </span>
          )
        },
        meta: { width: "35%" },
      },
      {
        header: `Target Text (${selectedTargetName})`,
        cell: ({ row }) => {
          const absoluteIndex = pageIndex * pageSize + row.index;

          // Use the ref — this is always up-to-date without causing re-renders
          const value = tableDataRef.current[absoluteIndex]?.targetText || "";

          if (!row.original.translatedText) {
            return (
              <span className="block w-full text-center">Not Available</span>
            )
          }

          return (
            <textarea
              rows={3}
              className="min-h-[60px] w-full border border-gray-300 rounded-md text-gray-700 p-2
          focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none resize-y"
              value={value}
              onChange={(e) => {
                const newValue = e.target.value;

                // Update state (triggers re-render)
                setTableData(prev => {
                  const updated = [...prev];
                  updated[absoluteIndex] = {
                    ...updated[absoluteIndex],
                    targetText: newValue,
                  };
                  return updated;
                });

                // Also immediately update the ref so next render sees correct value
                tableDataRef.current[absoluteIndex].targetText = newValue;
                markRowAsEdited(row.original.sentenceId);
              }}
              placeholder="Enter translation..."
            />
          );
        },
        meta: { width: "35%" },
      },
      {
        header: "Student Status",
        accessorKey: "studentStatus",
        cell: ({ row }) => (
          <span
            className={`font-semibold ${row.original.studentStatus === "PENDING"
              ? "text-yellow-600"
              : row.original.studentStatus === "VALIDATED"
                ? "text-green-600"
                : "text-gray-800"
              }`}
          >
            {row.original.studentStatus || "PENDING"}
          </span>
        ),
        meta: { width: "10%" },
      },
      role === "REVIEWER"
        ? {
          header: "Reviewer Status",
          accessorKey: "reviewerStatus",
          cell: ({ row }) => (
            <span
              className={`font-semibold ${row.original.reviewerStatus === "PENDING"
                ? "text-yellow-600"
                : row.original.reviewerStatus === "VALIDATED"
                  ? "text-green-600"
                  : "text-gray-800"
                }`}
            >
              {row.original.reviewerStatus || "PENDING"}
            </span>
          ),
          meta: { width: "10%" },
        }
        : null,
      {
        header: "Actions",
        cell: ({ row }) => {
          const absoluteIndex = pageIndex * pageSize + row.index;
          const isSelected = absoluteIndex === selectedRow;

          const sentenceId = row.original.sentenceId;
          const isValidated = row.original.studentStatus === 'VALIDATED';
          const isEdited = isRowEdited(sentenceId);
          console.log(row.original.reviewerStatus === 'VALIDATED')

          return (
            <div className="flex flex-col gap-2">
              {row.original.studentStatus === 'REVIEWED' ?
                <>
                  <button onClick={() => discardSentence(row)}
                    className={`px-4 py-1.5 rounded-md text-sm font-semibold text-white shadow-md transition-all bg-green-600 hover:bg-green-700`}>
                    Discard Sentence
                  </button>
                  <button
                    onClick={() => markAsResolved(row)}
                    className="px-4 py-1.5 rounded-md text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 shadow-md hover:shadow-lg transition-all"
                  >
                    Mark as Resolved
                  </button>
                </>
                :
                <>
                  <button onClick={() => validateSentence(row)}
                    className={`px-4 py-1.5 rounded-md text-sm font-semibold text-white shadow-md transition-all ${row.original.reviewerStatus === 'VALIDATED' ? 'cursor-not-allowed bg-gray-400 hover:bg-gray-500' : 'bg-green-600 hover:bg-green-700'}`}>
                    {row.original.reviewerStatus === 'VALIDATED' ? 'Validated' : row.original.reviewerStatus === 'SAVED' ? "Submit" : "Validate"}
                  </button>
                  {row.original.reviewerStatus === 'PENDING' && <button
                    onClick={() => rejectSentence(row)} className="px-4 py-1.5 rounded-md text-sm font-semibold text-white bg-red-500 hover:bg-red-600 shadow-md hover:shadow-lg transition-all"
                  >Reject
                  </button>}
                </>
              }
            </div>
          );
        }
      }
    ]

    return columns.filter(Boolean);
  }, [pageIndex, pageSize, selectedTargetName, selectedRow, role]);
  // ← tableData REMOVED from deps!

  const rejectedColumns = React.useMemo(() => {
    const columns = [
      {
        header: "Sr. No.",
        cell: ({ row }) => pageIndex * pageSize + row.index + 1,
        meta: { width: "10%" },
      },
      {
        header: "Source Text (English)",
        cell: ({ row }) => (
          <span onClick={() => {
            if (row.original.sentenceColor === 'blue') {
              showReviewComments(row);
            }
          }}
            className={`font-medium block whitespace-pre-wrap ${row.original.sentenceColor === 'blue' ? `text-blue-500 hover:text-blue-600 cursor-pointer` : row.original.sentenceColor === 'green' ? `text-green-500 hover:text-green-600` : `text-gray-800`}`}>
            {row.original.sourceText}
          </span>
        ),
        meta: { width: "35%" },
      },
      {
        header: `Target Text (${selectedTargetName})`,
        cell: ({ row }) => {
          const absoluteIndex = pageIndex * pageSize + row.index;

          // Use the ref — this is always up-to-date without causing re-renders
          const value = tableDataRef.current[absoluteIndex]?.targetText || "";

          if (!editSentencesTTS) {
            return (<span>{row.original.translatedText}</span>
            )
          }

          return (
            <textarea
              rows={3}
              className="min-h-[60px] w-full border border-gray-300 rounded-md text-gray-700 p-2
          focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none resize-y"
              value={value}
              onChange={(e) => {
                const newValue = e.target.value;

                // Update state (triggers re-render)
                setTableData(prev => {
                  const updated = [...prev];
                  updated[absoluteIndex] = {
                    ...updated[absoluteIndex],
                    targetText: newValue,
                  };
                  return updated;
                });

                // Also immediately update the ref so next render sees correct value
                tableDataRef.current[absoluteIndex].targetText = newValue;
                markRowAsEdited(row.original.sentenceId);
              }}
              placeholder="Enter translation..."
            />
          );
        },
        meta: { width: "35%" },
      },
      {
        header: "Student Status",
        accessorKey: "studentStatus",
        cell: ({ row }) => (
          <span
            className={`font-semibold ${row.original.studentStatus === "PENDING"
              ? "text-yellow-600"
              : row.original.studentStatus === "VALIDATED" || row.original.studentStatus === "RESOLVED"
                ? "text-green-600"
                : row.original.studentStatus === "REJECTED"
                  ? "text-red-600"
                  : "text-gray-800"
              }`}
          >
            {row.original.studentStatus || "PENDING"}
          </span>
        ),
        meta: { width: "10%" },
      },
      role === "REVIEWER"
        ? {
          header: "Reviewer Status",
          accessorKey: "reviewerStatus",
          cell: ({ row }) => (
            <span
              className={`font-semibold ${row.original.reviewerStatus === "PENDING"
                ? "text-yellow-600"
                : row.original.reviewerStatus === "VALIDATED" || row.original.reviewerStatus === "RESOLVED"
                  ? "text-green-600"
                  : row.original.reviewerStatus === "REJECTED"
                    ? "text-red-600"
                    : "text-gray-800"
                }`}
            >
              {row.original.reviewerStatus || "PENDING"}
            </span>
          ),
          meta: { width: "10%" },
        }
        : null,
      role !== "REVIEWER" ? {
        header: "Actions",
        cell: ({ row }) => {
          const absoluteIndex = pageIndex * pageSize + row.index;
          const isSelected = absoluteIndex === selectedRow;

          const sentenceId = row.original.sentenceId;
          const isValidated = row.original.studentStatus === 'VALIDATED';
          const isEdited = isRowEdited(sentenceId);           // ← uses ref
          const shouldBeActive = !isValidated || isEdited;

          return (
            role === 'REVIEWER' ?
              <div className="flex flex-col gap-2">
                {row.original.sentenceColor === 'blue' ?
                  <>
                    <button onClick={() => discardSentence(row)}
                      className={`px-4 py-1.5 rounded-md text-sm font-semibold text-white shadow-md transition-all bg-green-600 hover:bg-green-700`}>
                      Discard Sentence
                    </button>
                    <button
                      onClick={() => markAsResolved(row)}
                      className="px-4 py-1.5 rounded-md text-sm font-semibold text-white bg-amber-600 hover:bg-amber-700 shadow-md hover:shadow-lg transition-all"
                    >
                      Mark as Resolved
                    </button>
                  </>
                  :
                  <>
                    <button onClick={() => validateSentence(row)}
                      className={`px-4 py-1.5 rounded-md text-sm font-semibold text-white shadow-md transition-all ${row.original.reviewerStatus === 'VALIDATED' ? 'cursor-not-allowed bg-gray-400 hover:bg-gray-500' : 'bg-green-600 hover:bg-green-700'}`}>
                      {row.original.reviewerStatus === 'VALIDATED' ? 'Validated' : row.original.reviewerStatus === 'SAVED' ? "Submit" : "Validate"}
                    </button>
                    <button
                      onClick={() => rejectSentence(row)} className="px-4 py-1.5 rounded-md text-sm font-semibold text-white bg-red-500 hover:bg-red-600 shadow-md hover:shadow-lg transition-all"
                    >Reject
                    </button>
                  </>
                }
              </div>
              :
              <div className="flex flex-col gap-2">
                <button onClick={() => validateSentence(row)}
                  disabled={!shouldBeActive || !editSentencesTTS}
                  className={`px-4 py-1.5 rounded-md text-sm font-semibold text-white shadow-md transition-all ${!shouldBeActive || !editSentencesTTS ? 'cursor-not-allowed bg-gray-400 hover:bg-gray-500' : 'bg-green-600 hover:bg-green-700'}`}>
                  {shouldBeActive ? 'Validate' : 'Validated'}
                </button>
              </div>
          );
        }
      } : null
    ]

    return columns.filter(Boolean);
  }, [pageIndex, pageSize, selectedTargetName, selectedRow, role]);



  return (
    <div className="bg-[#fffaf7] min-h-screen">
      <Header />

      <div className="flex p-4">

        {/* Main Content */}
        <main
          className={`flex-1 pl-4 pr-4 bg-[#fffaf7] transition-all duration-300`}
        >
          <div className="bg-white shadow-lg rounded-2xl p-4 m-2 w-full">
            {/* Top Bar */}
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-semibold text-gray-800">
                  Translation Dashboard (Text to Text)
                </h2>
              </div>
            </div>

            {showMarkedSentencesTTT && (
              <div className="flex gap-4 mb-6">
                <button
                  onClick={() => setActiveTab("active")}
                  className={`px-4 py-2 rounded-lg font-medium transition ${activeTab === "active"
                    ? "bg-amber-600 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                >
                  Sentences
                </button>

                <button
                  onClick={() => setActiveTab("rejected")}
                  className={`px-4 py-2 rounded-lg font-medium transition ${activeTab === "rejected"
                    ? "bg-red-600 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                >
                  Sentences marked as issue
                </button>
              </div>
            )}


            {/* Data Table */}
            {files && files.length > 0 ? (
              <div className="border border-gray-200 rounded-xl">
                <GenericTableStudent
                  role={role}
                  data={paginatedData}
                  columns={role === 'REVIEWER' ? activeTab === 'active' ? reviewerfileColumns : rejectedColumns : activeTab === 'active' ? fileColumns : rejectedColumns}
                  showPagination={true}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  setDoubleClickedRow={setDoubleClickedRow}
                  getRowProps={(row) => {
                    const absoluteIndex = pageIndex * pageSize + row.index;
                    const isSelected = absoluteIndex === selectedRow;
                    return {
                      onClick: () => setSelectedRow(absoluteIndex),
                      style: {
                        backgroundColor: isSelected ? "#FFF6E5" : "white", // Highlight row
                        transition: "background 0.2s ease-in-out"
                      }
                    };
                  }}

                  hasNextPage={(pageIndex + 1) * pageSize < files.length}
                  onPaginationChange={({ pageIndex, pageSize }) => {
                    setPageIndex(pageIndex);
                    setPageSize(pageSize);
                  }}
                />
              </div>
            ) : (
              <div className="text-center text-gray-500 py-10 text-lg">
                No {activeTab === 'active' ? 'sentences' : 'flagged sentences'} data available for this lot.
              </div>
            )}
          </div>
        </main>
      </div>
      <ReviewModal
        isOpen={reviewModal.isOpen}
        selectedTargetName={selectedTargetName}
        row={reviewModal.row}
        comment={reviewModal.comment}
        translatedText={
          reviewModal.row
            ? tableDataRef.current[pageIndex * pageSize + reviewModal.row.index]?.targetText || ""
            : ""
        }
        onCommentChange={handleCommentChange}
        onClose={closeReviewModal}
        onSubmit={handleReviewSubmit}
        loading={loading}
      />
      <CommentsModal
        isOpen={commentsModal.isOpen}
        comments={commentsModal.comments}
        loading={commentsModal.loading}
        onClose={() => setCommentsModal({ isOpen: false, sentenceId: null, comments: [], loading: false })}
      />

      <DiscardModal
        isOpen={discardModal.isOpen}
        row={discardModal.row}
        loading={discardModal.loading}
        onConfirm={async () => {
          setDiscardModal(prev => ({ ...prev, loading: true }));
          await discardSentence(discardModal.row); // your existing function
          setDiscardModal({ isOpen: false, row: null, loading: false });
          setDoubleClickedRow(null);
        }}
        onCancel={() => {
          setDiscardModal({ isOpen: false, row: null, loading: false });
          setDoubleClickedRow(null)
          // Don't clear selection — let user try again
        }}
      />
    </div>
  );
};

export default TTTCard;
